import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, Truck } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { t, getProductName } from '../lib/translations';
import { SheetClose } from './ui/sheet';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartSheet = () => {
  const navigate = useNavigate();
  const { isAuthenticated, language } = useAuth();
  const { cart, updateQuantity, removeFromCart, loading } = useCart();

  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [freeDeliveryAbove, setFreeDeliveryAbove] = useState(0);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const formatPrice = (price) => `₹${Number(price).toFixed(2)}`;

  const subtotal = cart.total ?? 0;

  const appliedDeliveryCharge = (() => {
    if (deliveryCharge <= 0) return 0;
    if (freeDeliveryAbove > 0 && subtotal >= freeDeliveryAbove) return 0;
    return deliveryCharge;
  })();

  const orderTotal = subtotal + appliedDeliveryCharge;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        setDeliveryCharge(data.delivery_charge ?? 0);
        setFreeDeliveryAbove(data.free_delivery_above ?? 0);
      } catch (err) {
        console.error('Failed to fetch delivery settings:', err);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
        <ShoppingBag className="w-16 h-16 text-slate-300 mb-4" />
        <p className="text-slate-600 mb-4">Login to view your cart</p>
        <SheetClose asChild>
          <Button onClick={() => navigate('/login')} className="bg-green-600 hover:bg-green-700">
            {t('login')}
          </Button>
        </SheetClose>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
        <ShoppingBag className="w-16 h-16 text-slate-300 mb-4" />
        <p className="text-slate-600 mb-4">{t('emptyCart')}</p>
        <SheetClose asChild>
          <Button onClick={() => navigate('/products')} className="bg-green-600 hover:bg-green-700">
            {t('continueShopping')}
          </Button>
        </SheetClose>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="cart-sheet">
      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {cart.items.map((item) => (
          <div
            key={item.product_id}
            className="flex gap-3 p-3 bg-slate-50 rounded-xl"
            data-testid={`cart-item-${item.product_id}`}
          >
            {/* Product Image */}
            <img
              src={item.product?.image || 'https://via.placeholder.com/80'}
              alt={item.product ? getProductName(item.product) : 'Product'}
              className="w-20 h-20 object-cover rounded-lg"
            />

            {/* Product Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-slate-900 truncate">
                {item.product ? getProductName(item.product) : 'Product'}
              </h4>
              <p className="text-sm text-slate-500">{item.product?.unit}</p>

              <div className="flex items-center justify-between mt-2">
                <span className="font-bold text-green-600">
                  {formatPrice((item.product?.discount_price || item.product?.price || 0) * item.quantity)}
                </span>

                {/* Quantity Controls */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                    disabled={loading}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-6 text-center font-medium">{item.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                    disabled={loading}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:text-red-600"
                    onClick={() => removeFromCart(item.product_id)}
                    disabled={loading}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer with Pricing Breakdown */}
      <div className="border-t border-slate-200 pt-4 space-y-3">

        {/* Subtotal */}
        <div className="flex justify-between items-center text-sm text-slate-600">
          <span>{language === 'hi' ? 'उप-योग' : 'Subtotal'}</span>
          <span>{formatPrice(subtotal)}</span>
        </div>

        {/* Delivery Charge */}
        {settingsLoading ? (
          <div className="flex justify-between items-center text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" />
              {language === 'hi' ? 'डिलीवरी शुल्क' : 'Delivery Charge'}
            </span>
            <span className="animate-pulse">...</span>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center text-sm">
              <span className="flex items-center gap-1.5 text-slate-600">
                <Truck className="w-3.5 h-3.5" />
                {language === 'hi' ? 'डिलीवरी शुल्क' : 'Delivery Charge'}
              </span>
              {appliedDeliveryCharge > 0 ? (
                <span className="font-medium text-slate-800">{formatPrice(appliedDeliveryCharge)}</span>
              ) : (
                <span className="text-green-600 font-semibold">
                  {language === 'hi' ? 'मुफ्त' : 'FREE'}
                </span>
              )}
            </div>

            {/* Free delivery progress bar */}
            {deliveryCharge > 0 && freeDeliveryAbove > 0 && appliedDeliveryCharge > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-slate-500">
                  {language === 'hi'
                    ? `₹${(freeDeliveryAbove - subtotal).toFixed(0)} और जोड़ें — मुफ्त डिलीवरी पाएं!`
                    : `Add ₹${(freeDeliveryAbove - subtotal).toFixed(0)} more for FREE delivery!`}
                </p>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((subtotal / freeDeliveryAbove) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Earned free delivery */}
            {deliveryCharge > 0 && freeDeliveryAbove > 0 && appliedDeliveryCharge === 0 && subtotal >= freeDeliveryAbove && (
              <p className="text-xs text-green-600 font-medium">
                🎉 {language === 'hi' ? 'आपने मुफ्त डिलीवरी अर्जित की!' : 'You earned free delivery!'}
              </p>
            )}
          </>
        )}

        {/* Divider */}
        <div className="border-t border-slate-100" />

        {/* Grand Total */}
        <div className="flex justify-between items-center">
          <span className="text-lg font-medium">{t('total')}</span>
          <span className="text-2xl font-bold text-green-600">{formatPrice(orderTotal)}</span>
        </div>

        <SheetClose asChild>
          <Button
            className="w-full h-12 bg-green-600 hover:bg-green-700 text-lg"
            onClick={() => navigate('/checkout')}
            data-testid="checkout-btn"
          >
            {t('checkout')}
          </Button>
        </SheetClose>
      </div>
    </div>
  );
};

export default CartSheet;
